import React, { FC, MouseEventHandler, SetStateAction, useEffect } from 'react';
import { get, isEmpty, isNil } from 'lodash';
import {
  Card,
  Typography,
  Grid,
  FormControlLabel,
  FormControl,
  RadioGroup,
  Radio,
  TextField,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import { injectIntl, IntlShape } from 'react-intl';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import { translate } from 'containers/services/translate';
import {
  CONTACTED_VICTIM_ID,
  REFUSED_CONTACT_VICTIM_ID,
} from 'containers/TransactionManagerTimeLine/ContactVictim/constants';
import messages from 'containers/TransactionManagerTimeLine/ContactVictim/messages';
import currentUserStorage from 'containers/services/security/current-user-storage';
import { toast } from 'react-toastify';
import PrimaryButton from 'components/Buttons/PrimaryButton';
import ImportedReportDialog from 'containers/TransactionManagerTimeLine/ContactVictim/ImportedReportDialog';
import { TRANSACTION_STEPS } from 'containers/TransactionManagerTimeLine/constants';
import { getPendingStepTimeLine } from 'containers/TransactionManagerTimeLine/utils';
import {
  StyledCard,
  StyledTitle,
  StyledSubTitle,
  StyledChoice,
  StyledTextField,
  IstyledIcon,
  StyledUpdate,
} from 'containers/TransactionManagerTimeLine/ContactVictim/style';
import {
  VictimType,
  TransactionType,
  TransactionContactType,
} from 'typings/globalTypes';

export interface ContactCardProps {
  intl: IntlShape;
  listings: any;
  requestAddContactTransaction: Function;
  contactTransaction: TransactionContactType;
  transactionId: number;
  claimId: number;
  transactionInfo: TransactionType;
  requestUpdateContactTransaction: Function;
  handleOpenDrawerMission: MouseEventHandler<HTMLButtonElement>;
  isConsultation?: boolean;
  expertJudiciaryExpertise: any;
  victimId: number;
  requestImportMissionExpertise: Function;
  judiciaryExpertReport: any;
  sagisVictimInfo: any;
  sagisOpposingParties: any;
  victim: VictimType;
  requestGetDocByDocumentumId: Function;
}

const ContactCard: FC<ContactCardProps> = props => {
  const [choiceContact, setChoiceContact] = React.useState(null);
  const [reason, setReason] = React.useState(null);
  const [readOnly, setReadOnly] = React.useState(false);
  const [toUpdate, setToUpdate] = React.useState(false);
  const [
    openImportedReportDialog,
    setOpenImportedReportDialog,
  ] = React.useState(false);

  const cleanStore = () => {
    setReadOnly(false);
    setChoiceContact(null);
    setReason(null);
  };

  useEffect(() => {
    cleanStore();
    if (
      !isEmpty(get(props, 'contactTransaction', {})) ||
      !isEmpty(
        getPendingStepTimeLine(
          props.transactionInfo,
          TRANSACTION_STEPS.VICTIM_INFO,
        ),
      ) ||
      !isEmpty(
        getPendingStepTimeLine(
          props.transactionInfo,
          TRANSACTION_STEPS.VICTIM_INFO_THIRD_VALIDATION,
        ),
      )
    ) {
      setReadOnly(true);
      setChoiceContact(
        //@ts-ignore
        String(
          get(props, 'contactTransaction.transactionContactChoiceId', null),
        ),
      );
      setReason(
        (get(
          props,
          'contactTransaction.comment',
          null,
        ) as unknown) as SetStateAction<null>,
      );
    }
  }, [props.contactTransaction, props.transactionId]);
  const handleChange = (
    event: React.ChangeEvent<{
      name?: string;
      value?: unknown;
    }>,
  ) => {
    setChoiceContact(event.target.value as any);
  };
  const handleChangeField = (
    event: React.ChangeEvent<{
      name?: string;
      value?: unknown;
    }>,
  ) => {
    setReason(event.target.value as any);
  };
  const successCallback = () => {
    toast.success(translate(messages.successAddContact, props));
    setToUpdate(false);
  };
  const handleValidate = () => {
    if (toUpdate && !isNil(get(props.contactTransaction, 'id', null))) {
      props.requestUpdateContactTransaction(
        {
          claimId: props.claimId,
          transactionId: props.transactionId,
          transactionContactChoiceId: Number(choiceContact),
          comment: reason,
          creator: currentUserStorage.get().username,
          id: get(props.contactTransaction, 'id'),
          isTrackingActive: featureService.hasFeature(
            FEATURES.TRACKING_ACTIONS_USER,
          ),
        },
        successCallback,
      );
    } else {
      props.requestAddContactTransaction(
        {
          transactionContact: {
            claimId: props.claimId,
            transactionId: props.transactionId,
            transactionContactChoiceId: Number(choiceContact),
            comment: reason,
            creator: currentUserStorage.get().username,
          },
          isTrackingActive: featureService.hasFeature(
            FEATURES.TRACKING_ACTIONS_USER,
          ),
          isTransactionInboxesActive: featureService.hasFeature(
            FEATURES.INBOXES_TRANSACTION_MANAGER,
          ),
        },
        successCallback,
      );
    }
  };
  const activateEdit = () => {
    setReadOnly(false);
    setToUpdate(true);
  };
  const filtreJudiciaryExpertExpertise = () => {
    if (!isEmpty(get(props, 'judiciaryExpertReport', []))) {
      return get(props, 'judiciaryExpertReport', []).filter(
        (item: any) =>
          !isEmpty(item.victimExpertises) &&
          !isEmpty(
            item.victimExpertises.filter(
              (el: any) => el.victimId === props.victimId,
            ),
          ),
      );
    }
  };

  const filtredExpertJudiciaryExpertise = () => {
    if (!isEmpty(props.expertJudiciaryExpertise)) {
      const expertiseMissionId = props.expertJudiciaryExpertise
        .filter(
          (item: any) =>
            !isNil(item.missionVictimId) &&
            !isEmpty(
              item.mission.victims.filter(
                (el: any) => el.id === props.victimId,
              ),
            ),
        )
        .map((el: any) => el.mission.id);
      return props.expertJudiciaryExpertise.filter(
        (item: any) =>
          !isNil(item.missionVictimId) &&
          !isEmpty(
            item.mission.victims.filter((el: any) => el.id === props.victimId),
          ) &&
          !expertiseMissionId.includes(
            get(item.mission, 'transactionMisson.importedMissionId'),
          ),
      );
    } else {
      return [];
    }
  };
  return (
    <Card style={StyledCard} elevation={0}>
      <Grid container direction="column" justify="space-between">
        <Grid item>
          <Grid container direction="row" justify="space-between">
            <Grid item>
              <Typography style={StyledTitle}>
                {translate(messages.transactionContactStep, props)}
              </Typography>
            </Grid>
            {!isEmpty(get(props, 'contactTransaction', {})) &&
              !props.isConsultation && (
                <Grid item>
                  <Grid
                    container
                    direction="row"
                    spacing={1}
                    data-testId={'edit'}
                    onClick={activateEdit}
                    style={{ cursor: 'pointer' }}
                  >
                    <Grid item>
                      <EditIcon style={IstyledIcon} />
                    </Grid>
                    <Grid item>
                      <Typography style={StyledUpdate}>
                        {translate(messages.updateTitle, props)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              )}
          </Grid>
        </Grid>
        <Grid item>
          <Grid container direction="row" spacing={1}>
            <Grid item>
              <Typography style={StyledSubTitle}>
                {translate(messages.transactionContactSubTitle, props)}
              </Typography>
              <FormControl style={StyledChoice}>
                <RadioGroup row onChange={handleChange} value={choiceContact}>
                  {get(props.listings, 'transactionContactChoice', []).map(
                    (el: any) => (
                      <FormControlLabel
                        control={
                          <Radio color="primary" value={String(el.id)} />
                        }
                        label={el.title}
                        disabled={readOnly}
                      />
                    ),
                  )}
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={3}>
              {String(choiceContact) === REFUSED_CONTACT_VICTIM_ID && (
                <TextField
                  name="reason"
                  value={reason}
                  onChange={handleChangeField}
                  label={translate(messages.reasonInputTitle, props)}
                  style={StyledTextField}
                  disabled={readOnly}
                />
              )}
            </Grid>
            <Grid
              item
              xs
              style={{
                marginTop: '40px',
                position: 'absolute',
                right: '50px',
              }}
            >
              {(isEmpty(props.contactTransaction) ||
                (toUpdate && !isEmpty(props.contactTransaction))) &&
                !readOnly &&
                !props.isConsultation && (
                  <PrimaryButton
                    onClick={handleValidate}
                    disableElevation
                    titlecolor="#ffffff"
                    background="#0075c9"
                    border="#0075c9"
                    data-testId="validate-button"
                    title={translate(messages.confirm, props)}
                    disabled={
                      isNil(choiceContact) ||
                      (!isNil(choiceContact) &&
                        String(choiceContact) === REFUSED_CONTACT_VICTIM_ID &&
                        isNil(reason))
                    }
                  />
                )}
              {!isEmpty(props.contactTransaction) &&
                String(choiceContact) === CONTACTED_VICTIM_ID &&
                !toUpdate &&
                featureService.hasFeature(FEATURES.TRANSACTION_V2) &&
                !props.isConsultation &&
                !isEmpty(filtreJudiciaryExpertExpertise()) &&
                readOnly && (
                  <PrimaryButton
                    onClick={() => {
                      setOpenImportedReportDialog(true);
                    }}
                    disableElevation
                    titlecolor="#0075c9"
                    background="unset"
                    border="unset"
                    data-testId="add-expert-judiciary"
                    title={translate(messages.addJudiciaryButtonTitle, props)}
                    style={{
                      marginRight: '10px',
                      backgroundColor: 'rgb(245, 247, 250) !important',
                    }}
                  />
                )}
              {!isEmpty(props.contactTransaction) &&
                String(choiceContact) === CONTACTED_VICTIM_ID &&
                !toUpdate &&
                !props.isConsultation &&
                readOnly && (
                  <PrimaryButton
                    onClick={props.handleOpenDrawerMission}
                    disableElevation
                    titlecolor="#ffffff"
                    background="#0075c9"
                    border="#0075c9"
                    data-testId="assign-expert"
                    title={translate(messages.assignExpertButtonTitle, props)}
                  />
                )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <ImportedReportDialog
        requestImportMissionExpertise={props.requestImportMissionExpertise}
        open={openImportedReportDialog}
        transactionId={props.transactionId}
        onClose={() => setOpenImportedReportDialog(false)}
        handleValidate={() => {}}
        judiciaryExpertisesVictim={filtredExpertJudiciaryExpertise()}
        expertJudiciaryExpertise={filtreJudiciaryExpertExpertise()}
        sagisVictimInfo={props.sagisVictimInfo}
        listings={props.listings}
        claimId={props.claimId}
        sagisOpposingParties={props.sagisOpposingParties}
        victim={props.victim}
        transactionInfo={props.transactionInfo}
        requestGetDocByDocumentumId={props.requestGetDocByDocumentumId}
      />
    </Card>
  );
};

export default injectIntl(ContactCard);
